import type { FC } from 'react';
import { memo, useEffect, useRef } from 'react';
import { twMerge } from 'tailwind-merge';

import { QuickSearch } from '../../../../front/src/components/QuickSearch/QuickSearch';
import { defaultHeaderHeight, useHeaderHeight } from '../../../../front/src/hooks/useHeaderHeight';
import { usePermissions } from '../../../../front/src/hooks/usePermissions';
import type { PageConfig } from '../../../../front/src/types/debate-types';
import { isBrowser } from '../../../../front/src/utils/web';
import { HeaderLogin } from './HeaderLogin';
import { HeaderLogo } from './HeaderLogo';
import { HeaderMenu } from './HeaderMenu';

interface Props {
  pageConfig: PageConfig | undefined;
  dynamicStickyHeaderHeight?: number;
}

// Header background: the tenant theme sets `--gc-header-bg` (via a custom `head` CSS block, like
// `--color-primary`); otherwise the default gradient. The rubrique menu (HeadingsNavBar) uses
// the SAME value so a sticky menu merges seamlessly with the sticky navbar above it (B3_02).
const HEADER_BG = 'var(--gc-header-bg, linear-gradient(to right, #F2F9FB, #F3F2FB))';

const HeaderNavbar: FC<Props> = memo(function HeaderNavbar(props) {
  const { pageConfig, dynamicStickyHeaderHeight = 0 } = props;
  const { setHeight } = useHeaderHeight();
  const { authUserId, isAuthDone } = usePermissions();
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    setHeight(ref.current?.offsetHeight ?? defaultHeaderHeight);
  }, [setHeight]);

  const shouldDisplayHeaderMenu = isAuthDone && isBrowser && authUserId;
  const shouldDisplayLoginButton = isAuthDone && (!isBrowser || !authUserId);

  return (
    <header
      ref={ref}
      id="NavbarHeader"
      className="navbarSticky py-[15px] sm:py-[21px] flex justify-center px-[18px] sticky z-[400]"
      style={{ top: dynamicStickyHeaderHeight, backgroundImage: HEADER_BG }}
    >
      <div className="NavbarHeader_content flex-1 flex justify-between items-center max-w-[1024px] margin-auto relative">
        <div className="NavbarHeader_logoAndSearch flex flex-1">
          <div
            className={twMerge(
              'NavbarHeader_logoContainer flex items-center justify-center _md:border-r _md:border-gray-250 _md:pr-4',
            )}
          >
            <HeaderLogo pageConfig={pageConfig} />
          </div>

          {/* In v2, h-10 was added. Add it here too? */}
          <div className="NavbarHeader_searchContainer md:flex-1 ml-8 mr-6 _md:ml-2 flex justify-start items-center max-w-[600px] h-10">
            <QuickSearch />
          </div>
        </div>

        <div className="NavbarHeader_connectContainer flex justify-end">
          {shouldDisplayHeaderMenu && <HeaderMenu />}
          {shouldDisplayLoginButton && <HeaderLogin />}
        </div>
      </div>
    </header>
  );
});

export default HeaderNavbar;
